import React, { useState } from 'react';
import { Download, FileSpreadsheet, FileText, X } from 'lucide-react';
import { SubFeatureGate } from './FeatureGate';
import api from '../api';
import './AttendanceExportControls.css';

const initialFilters = {
  dateFrom: '',
  dateTo: '',
  session: ''
};

const getDownloadName = (assignmentId, format) => `attendance_report_${assignmentId}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;

function AttendanceExportControls({ assignmentId, batch }) {
  const [filters, setFilters] = useState(initialFilters);
  const [isOpen, setIsOpen] = useState(false);
  const [exporting, setExporting] = useState('');
  const [error, setError] = useState('');

  const updateFilter = (event) => {
    const { name, value } = event.target;
    setFilters(current => ({ ...current, [name]: value }));
    setError('');
  };

  const exportAttendance = async (format) => {
    if (!assignmentId || exporting) return;
    if (filters.dateFrom && filters.dateTo && filters.dateFrom > filters.dateTo) {
      setError('Start date cannot be after end date.');
      return;
    }

    setExporting(format);
    setError('');
    try {
      const response = await api.get(`/faculty/lms/assigned/${assignmentId}/attendance-export`, {
        params: {
          format,
          batch: batch || undefined,
          date_from: filters.dateFrom || undefined,
          date_to: filters.dateTo || undefined,
          session: filters.session || undefined
        },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = getDownloadName(assignmentId, format);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (exportError) {
      setError('Attendance export failed. Please try again.');
    } finally {
      setExporting('');
    }
  };

  return (
    <SubFeatureGate feature="lms" sub="lms_attendance_export">
    <div className="attendance-export">
      <button type="button" className="attendance-export-trigger" onClick={() => setIsOpen(true)}>
        <Download size={16} /> Export
      </button>
      {isOpen && <div className="attendance-export-backdrop" role="presentation" onMouseDown={(event) => {
        if (event.target === event.currentTarget) setIsOpen(false);
      }}>
        <section className="attendance-export-controls" role="dialog" aria-modal="true" aria-labelledby="attendance-export-title">
          <div className="attendance-export-heading">
            <div>
              <h3 id="attendance-export-title">Export attendance report</h3>
              <p>Choose the report range and format.</p>
            </div>
            <button type="button" className="attendance-export-close" onClick={() => setIsOpen(false)} aria-label="Close export dialog">
              <X size={18} />
            </button>
          </div>
          <div className="attendance-export-filters">
        <label>
          From
          <input type="date" name="dateFrom" value={filters.dateFrom} onChange={updateFilter} />
        </label>
        <label>
          To
          <input type="date" name="dateTo" value={filters.dateTo} onChange={updateFilter} />
        </label>
        <label>
          Session
          <select name="session" value={filters.session} onChange={updateFilter}>
            <option value="">AM + PM</option>
            <option value="AM">AM only</option>
            <option value="PM">PM only</option>
          </select>
        </label>
        <label className="attendance-export-batch">
          Batch
          <input value={batch || 'All Batches'} readOnly aria-label="Selected batch" />
        </label>
          </div>
          <div className="attendance-export-actions">
        <span className="attendance-export-label"><Download size={15} /> Download as</span>
        <button type="button" onClick={() => exportAttendance('xlsx')} disabled={Boolean(exporting)}>
          <FileSpreadsheet size={15} /> {exporting === 'xlsx' ? 'Preparing...' : 'Excel'}
        </button>
        <button type="button" onClick={() => exportAttendance('pdf')} disabled={Boolean(exporting)}>
          <FileText size={15} /> {exporting === 'pdf' ? 'Preparing...' : 'PDF'}
        </button>
          </div>
          {error && <span className="attendance-export-error" role="alert">{error}</span>}
        </section>
      </div>}
    </div>
    </SubFeatureGate>
  );
}

export default AttendanceExportControls;
